/**
 * SIP Ladder Diagram Component
 * 
 * Displays SIP call flows as a proper ladder/sequence diagram with:
 * - Vertical entity columns (SBC in center, peers on sides)
 * - Horizontal arrows showing message flow direction
 * - Arrow labels with peer names and SIP methods
 */

import { useMemo } from 'react';
import { Server } from 'lucide-react';
import type { SipTrafficEvent } from '../../services/api';

type SipTrafficMessage = SipTrafficEvent;

interface SipLadderDiagramProps {
  events: SipTrafficMessage[];
  sipPeerNameMap: Map<string, string>;
  showAllDetails?: boolean;
  onEventClick?: (event: SipTrafficMessage) => void;
}

// Entity representing a participant in the call flow
interface Entity {
  id: string;
  name: string;
  type: 'sbc' | 'peer';
  position: number; // 0 = leftmost, higher = more right
}

export function SipLadderDiagram({ events, sipPeerNameMap, showAllDetails = false, onEventClick }: SipLadderDiagramProps) {
  // Extract unique entities from events with SBC always in the middle
  const entities = useMemo<Entity[]>(() => {
    const peerIds = new Set<string>();
    
    // Collect all unique peer IDs
    for (const event of events) {
      if (event.sipPeerId) {
        peerIds.add(event.sipPeerId);
      }
    }
    
    const peerList = Array.from(peerIds);
    const result: Entity[] = [];
    
    // Split peers: half on left, half on right of SBC
    const leftPeers = peerList.slice(0, Math.ceil(peerList.length / 2));
    const rightPeers = peerList.slice(Math.ceil(peerList.length / 2));
    
    // Add left peers
    leftPeers.forEach((peerId, index) => {
      result.push({
        id: peerId,
        name: sipPeerNameMap.get(peerId) || `Peer ${index + 1}`,
        type: 'peer',
        position: index,
      });
    });
    
    // Add SBC in the middle
    result.push({
      id: 'sbc',
      name: 'SBCLite',
      type: 'sbc',
      position: leftPeers.length,
    });
    
    // Add right peers
    rightPeers.forEach((peerId, index) => {
      result.push({
        id: peerId,
        name: sipPeerNameMap.get(peerId) || `Peer ${leftPeers.length + index + 2}`,
        type: 'peer',
        position: leftPeers.length + 1 + index,
      });
    });
    
    // Sort by position
    return result.sort((a, b) => a.position - b.position);
  }, [events, sipPeerNameMap]);

  const getHeaderValue = (headers: Record<string, string | string[]> | null | undefined, key: string) => {
    if (!headers) return undefined;
    return headers[key] ?? headers[key.toLowerCase()] ?? headers[key.toUpperCase()];
  };

  const formatCSeq = (event: SipTrafficMessage) => {
    const raw = getHeaderValue(event.headers, 'cseq');
    if (!raw) return undefined;
    const match = String(raw).match(/(\d+)\s+(\w+)/);
    if (!match) return String(raw);
    return `${match[1]} ${match[2]}`;
  };

  const formatRSeq = (event: SipTrafficMessage) => {
    const raw = getHeaderValue(event.headers, 'rseq');
    if (!raw) return undefined;
    return String(raw).trim();
  };

  // Format event label (INVITE, 200 OK, etc.)
  const formatEventLabel = (event: SipTrafficMessage) => {
    const base = event.sipMethod
      ? event.sipMethod
      : event.responseCode
        ? `${event.responseCode} ${event.reasonPhrase || ''}`.trim()
        : 'Unknown';

    if (!showAllDetails) {
      return base;
    }

    const cseq = formatCSeq(event);
    const rseq = formatRSeq(event);
    const parts = [base];
    if (cseq) parts.push(`CSeq ${cseq}`);
    if (rseq) parts.push(`RSeq ${rseq}`);
    return parts.join(' · ');
  };

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false 
    }) + '.' + date.getMilliseconds().toString().padStart(3, '0');
  };

  // Get arrow color based on message type
  const getArrowColor = (event: SipTrafficMessage) => {
    if (event.responseCode) {
      if (event.responseCode < 200) return 'text-gray-500'; // Provisional
      if (event.responseCode < 300) return 'text-green-600'; // Success
      if (event.responseCode < 400) return 'text-yellow-600'; // Redirect
      return 'text-red-600'; // Error
    }
    // Request methods
    if (event.sipMethod === 'INVITE') return 'text-blue-600';
    if (event.sipMethod === 'BYE' || event.sipMethod === 'CANCEL') return 'text-orange-600';
    if (event.sipMethod === 'ACK') return 'text-green-600';
    return 'text-gray-600';
  };

  // Get background color for event label
  const getLabelBgColor = (event: SipTrafficMessage) => {
    if (event.responseCode) {
      if (event.responseCode < 200) return 'bg-gray-100 text-gray-700';
      if (event.responseCode < 300) return 'bg-green-100 text-green-700';
      if (event.responseCode < 400) return 'bg-yellow-100 text-yellow-700';
      return 'bg-red-100 text-red-700';
    }
    if (event.sipMethod === 'INVITE') return 'bg-blue-100 text-blue-700';
    if (event.sipMethod === 'BYE' || event.sipMethod === 'CANCEL') return 'bg-orange-100 text-orange-700';
    if (event.sipMethod === 'ACK') return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-700';
  };


  // Find entity index by peer ID
  const getEntityIndex = (peerId: string | null | undefined): number => {
    if (!peerId) return entities.findIndex(e => e.type === 'sbc');
    const index = entities.findIndex(e => e.id === peerId);
    return index >= 0 ? index : entities.findIndex(e => e.type === 'sbc');
  };

  const sbcIndex = entities.findIndex(e => e.type === 'sbc');

  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No events to display
      </div>
    );
  }

  // Fixed width for timestamp column
  const timestampWidth = 100; // pixels

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[700px]">
        {/* Entity Headers */}
        <div className="flex border-b-2 border-gray-300 pb-3 mb-4">
          {/* Timestamp column spacer */}
          <div style={{ width: `${timestampWidth}px`, flexShrink: 0 }} />
          {/* Entity columns */}
          <div className="flex flex-1">
            {entities.map((entity) => (
              <div 
                key={entity.id} 
                className="flex-1 text-center"
              >
                <div className={`inline-flex flex-col items-center px-4 py-2 rounded-lg ${
                  entity.type === 'sbc' 
                    ? 'bg-blue-100 border-2 border-blue-300' 
                    : 'bg-gray-100 border border-gray-300'
                }`}>
                  {entity.type === 'sbc' ? (
                    <Server className="w-5 h-5 mb-1 text-blue-600" />
                  ) : (
                    <div className="w-5 h-5 mb-1 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold">
                      {entity.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className={`text-sm font-medium ${
                    entity.type === 'sbc' ? 'text-blue-700' : 'text-gray-700'
                  }`}>
                    {entity.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vertical Lines */}
        <div className="relative">
          {/* Draw vertical lines for each entity */}
          <div className="absolute inset-0 flex pointer-events-none" style={{ zIndex: 0 }}>
            {/* Timestamp column spacer */}
            <div style={{ width: `${timestampWidth}px`, flexShrink: 0 }} />
            {/* Entity column lines */}
            <div className="flex flex-1">
              {entities.map((entity) => (
                <div 
                  key={entity.id} 
                  className="flex-1 flex justify-center"
                >
                  <div className={`w-0.5 h-full ${
                    entity.type === 'sbc' ? 'bg-blue-300' : 'bg-gray-300'
                  }`} />
                </div>
              ))}
            </div>
          </div>

          {/* Event Rows */}
          <div className="relative space-y-1" style={{ zIndex: 1 }}>
            {events.map((event) => {
              const peerIndex = getEntityIndex(event.sipPeerId);
              const isInbound = event.direction === 'inbound';
              
              // For inbound: arrow from peer to SBC
              // For outbound: arrow from SBC to peer
              const fromIndex = isInbound ? peerIndex : sbcIndex;
              const toIndex = isInbound ? sbcIndex : peerIndex;
              
              // Calculate arrow positioning
              const leftIndex = Math.min(fromIndex, toIndex);
              const rightIndex = Math.max(fromIndex, toIndex);
              const arrowGoesRight = fromIndex < toIndex;
              
              // Calculate positions as percentages of the entity area (excluding timestamp)
              // Each column center is at (index + 0.5) / totalColumns
              const leftPercent = ((leftIndex + 0.5) / entities.length) * 100;
              const rightPercent = ((rightIndex + 0.5) / entities.length) * 100;
              const widthPercent = rightPercent - leftPercent;

              return (
                <div 
                  key={event.id} 
                  className="relative flex items-center py-2 hover:bg-gray-50 cursor-pointer group min-h-[48px]"
                  onClick={() => onEventClick?.(event)}
                >
                  {/* Timestamp on the left - fixed width */}
                  <div 
                    className="text-xs text-gray-400 font-mono pl-2 flex-shrink-0"
                    style={{ width: `${timestampWidth}px` }}
                  >
                    {formatTime(event.timestamp)}
                  </div>

                  {/* Arrow area - takes remaining space */}
                  <div className="flex-1 relative h-full">
                    {/* Arrow Container - positioned within the entity area */}
                    <div 
                      className="absolute flex items-center top-1/2 -translate-y-1/2"
                      style={{ 
                        left: `${leftPercent}%`, 
                        width: `${widthPercent}%`,
                      }}
                    >
                      {/* Arrow Line with Label */}
                      <div className="relative w-full flex items-center">
                        {/* Arrow shaft */}
                        <div className={`absolute inset-x-0 h-0.5 ${getArrowColor(event).replace('text-', 'bg-')}`} />
                        
                        {/* Arrow head */}
                        <div 
                          className={`absolute ${arrowGoesRight ? 'right-0' : 'left-0'} ${getArrowColor(event)}`}
                          style={{
                            width: 0,
                            height: 0,
                            borderTop: '6px solid transparent',
                            borderBottom: '6px solid transparent',
                            ...(arrowGoesRight 
                              ? { borderLeft: '10px solid currentColor' }
                              : { borderRight: '10px solid currentColor' }
                            ),
                          }}
                        />

                        {/* Label on the arrow */}
                        <div className="absolute inset-x-0 flex justify-center -top-4">
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded shadow-sm ${getLabelBgColor(event)}`}>
                            {formatEventLabel(event)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Transport badge on hover */}
                  <div className="absolute right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      event.transport === 'TLS' ? 'bg-green-100 text-green-700' :
                      event.transport === 'TCP' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {event.transport}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
